/**
 * Custom Currency Converter for Multi-Market Support
 * Handles currency conversion based on locale from URL
 * Supports: Bahrain (BHD), KSA (SAR), UAE (AED), Kuwait (KWD), Oman (OMR), Qatar (QAR)
 *
 * Features:
 * - Automatic locale detection from URL pattern (.com/en-bh, .com/ar-sa, etc.)
 * - Real-time price conversion with accurate exchange rates
 * - Proper currency formatting (right-to-left for Arabic)
 * - DOM mutation observer for dynamic content
 * - LocalStorage caching for performance
 * - Global API for external use
 */

class CurrencyConverter {
  constructor() {
    // Exchange rates relative to BHD (base currency)
    // Update these rates periodically based on live exchange rates
    this.currencies = {
      'en-bh': {
        code: 'BHD',
        symbol: 'BHD',
        country: 'Bahrain',
        countryAr: 'البحرين',
        rate: 1.0,
        direction: 'ltr',
        decimals: 3,
      },
      'ar-bh': {
        code: 'BHD',
        symbol: 'د.ب',
        country: 'Bahrain',
        countryAr: 'البحرين',
        rate: 1.0,
        direction: 'rtl',
        decimals: 3,
      },
      'en-sa': {
        code: 'SAR',
        symbol: 'SAR',
        country: 'Saudi Arabia',
        countryAr: 'المملكة العربية السعودية',
        rate: 9.95,
        direction: 'ltr',
        decimals: 2,
      },
      'ar-sa': {
        code: 'SAR',
        symbol: 'ر.س',
        country: 'Saudi Arabia',
        countryAr: 'المملكة العربية السعودية',
        rate: 9.95,
        direction: 'rtl',
        decimals: 2,
      },
      'en-ae': {
        code: 'AED',
        symbol: 'AED',
        country: 'United Arab Emirates',
        countryAr: 'الإمارات العربية المتحدة',
        rate: 9.75,
        direction: 'ltr',
        decimals: 2,
      },
      'ar-ae': {
        code: 'AED',
        symbol: 'د.إ',
        country: 'United Arab Emirates',
        countryAr: 'الإمارات العربية المتحدة',
        rate: 9.75,
        direction: 'rtl',
        decimals: 2,
      },
      'en-kw': {
        code: 'KWD',
        symbol: 'KWD',
        country: 'Kuwait',
        countryAr: 'الكويت',
        rate: 0.81,
        direction: 'ltr',
        decimals: 3,
      },
      'ar-kw': {
        code: 'KWD',
        symbol: 'د.ك',
        country: 'Kuwait',
        countryAr: 'الكويت',
        rate: 0.81,
        direction: 'rtl',
        decimals: 3,
      },
      'en-om': {
        code: 'OMR',
        symbol: 'OMR',
        country: 'Oman',
        countryAr: 'عمان',
        rate: 1.02,
        direction: 'ltr',
        decimals: 3,
      },
      'ar-om': {
        code: 'OMR',
        symbol: 'ر.ع.',
        country: 'Oman',
        countryAr: 'عمان',
        rate: 1.02,
        direction: 'rtl',
        decimals: 3,
      },
      'en-qa': {
        code: 'QAR',
        symbol: 'QAR',
        country: 'Qatar',
        countryAr: 'قطر',
        rate: 9.66,
        direction: 'ltr',
        decimals: 2,
      },
      'ar-qa': {
        code: 'QAR',
        symbol: 'ر.ق',
        country: 'Qatar',
        countryAr: 'قطر',
        rate: 9.66,
        direction: 'rtl',
        decimals: 2,
      },
    };

    // Base currency for all prices (assumed USD in Shopify)
    this.baseCurrency = 'USD';
    this.baseSymbol = '$';
    this.baseRate = 3.641; // 1 USD = 3.641 BHD (reference rate)

    // Price rounding mode: 'none' | '0.5' | '1.0'
    // Set via window.currencyRoundingMode (injected by theme.liquid from theme settings)
    this.roundingMode = window.currencyRoundingMode || 'none';

    // Initialize converter
    this.init();
  }

  init() {
    // Get current locale from URL
    this.currentLocale = this.getLocaleFromURL();

    // Store current locale
    this.setStoredLocale(this.currentLocale);

    // Check if we're in Arabic mode (need RTL formatting)
    const isArabic = this.currentLocale.startsWith('ar');
    const isEnglish = this.currentLocale.startsWith('en');

    // Only initialize currency conversion if NOT in Bahrain
    // Bahrain is the store's base currency, so no conversion needed
    // BUT we still need to format prices for correct display
    if (!this.currentLocale.endsWith('bh')) {
      // Multiple sweeps to catch all prices, including lazy-loaded and animated content
      // Homepage uses AOS (Animate On Scroll) which may delay element rendering
      setTimeout(() => {
        this.convertPrices();
      }, 500);

      setTimeout(() => {
        this.convertPrices();
      }, 1500);

      setTimeout(() => {
        this.convertPrices();
      }, 2500);

      this.observeDOM();
    } else if (isArabic) {
      // Arabic Bahrain - no conversion but need RTL-safe formatting (price before symbol)
      setTimeout(() => {
        this.formatPricesForRTL();
      }, 500);

      setTimeout(() => {
        this.formatPricesForRTL();
      }, 1500);

      this.observeDOM();
    } else if (isEnglish) {
      // English Bahrain - reformat to ensure "BHD X.XXX" format (symbol before price)
      setTimeout(() => {
        this.formatPricesForEnglish();
      }, 500);

      setTimeout(() => {
        this.formatPricesForEnglish();
      }, 1500);

      this.observeDOM();
    }

    // Update currency info in page (always do this)
    this.updateCurrencyInfo();

    // Listen for page changes (AJAX)
    this.setupCartObserver();

    // Override theme.Currency.formatMoney to apply currency conversion
    this.overrideThemeCurrencyFormatter();

    // Update country selector dropdowns with correct local currencies
    this.updateCountrySelectorCurrencies();

    // Log initialization
  }

  /**
   * Update native Shopify country selector dropdowns to show
   * each country's local currency instead of the store's base currency
   */
  updateCountrySelectorCurrencies() {
    // Map Shopify country ISO codes to our locale codes (English + Arabic)
    const countryToLocale = {
      BH: { en: 'en-bh', ar: 'ar-bh' },
      SA: { en: 'en-sa', ar: 'ar-sa' },
      AE: { en: 'en-ae', ar: 'ar-ae' },
      KW: { en: 'en-kw', ar: 'ar-kw' },
      OM: { en: 'en-om', ar: 'ar-om' },
      QA: { en: 'en-qa', ar: 'ar-qa' },
    };

    const self = this;

    /**
     * Build currency display text: "CODE عربي" (e.g. "BHD د.ب")
     * Shows English code on the left and Arabic symbol on the right
     */
    const getCurrencyDisplayText = (countryCode) => {
      const locales = countryToLocale[countryCode];
      if (!locales) return null;
      const enConfig = self.getCurrencyConfig(locales.en);
      const arConfig = self.getCurrencyConfig(locales.ar);
      if (!enConfig || !arConfig) return null;
      return `${enConfig.code} ${arConfig.symbol}`;
    };

    const updateAll = () => {
      document.querySelectorAll('localization-form').forEach((locForm) => {
        const countryInput = locForm.querySelector('input[name="country_code"]');
        if (!countryInput) return; // Skip language forms

        // Update each dropdown item to show local currency
        locForm.querySelectorAll('a[data-value]').forEach((link) => {
          const countryCode = link.dataset.value;
          const currencyText = getCurrencyDisplayText(countryCode);
          if (!currencyText) return;

          // Find or create the currency span
          let currencySpan = link.querySelector('.localization-form__currency');
          if (currencySpan) {
            // Update existing currency span and make it visible
            currencySpan.textContent = ` ${currencyText}`;
            currencySpan.classList.remove('hidden');
          } else {
            // Create a new currency span
            currencySpan = document.createElement('span');
            currencySpan.className = 'localization-form__currency motion-reduce';
            currencySpan.textContent = ` ${currencyText}`;
            link.appendChild(currencySpan);
          }
        });

        // Get the active country code by matching the URL locale
        let currentCountry = countryInput.value;
        const currentLocaleCode = self.currentLocale;
        for (const [code, locales] of Object.entries(countryToLocale)) {
          if (locales.en === currentLocaleCode || locales.ar === currentLocaleCode) {
            currentCountry = code;
            break;
          }
        }

        // Correct the active checkmark in dropdown list since native Shopify won't if market is fixed
        locForm.querySelectorAll('a[data-value]').forEach((link) => {
          const expectedCode = link.dataset.value;
          const isCurrent = expectedCode === currentCountry;

          if (isCurrent) {
            link.setAttribute('aria-current', 'true');
          } else {
            link.removeAttribute('aria-current');
          }

          const svgCheck = link.querySelector('span:not(.country):not(.localization-form__currency)');
          if (svgCheck) {
            if (isCurrent) {
              svgCheck.classList.remove('visibility-hidden');
            } else {
              svgCheck.classList.add('visibility-hidden');
            }
          }
        });

        // Update the button text with the current country's local currency
        const currencyText = getCurrencyDisplayText(currentCountry);
        if (currencyText) {
          const locales = countryToLocale[currentCountry];
          const enConfig = self.getCurrencyConfig(locales.en);
          const button = locForm.querySelector('button.localization-form__select');
          if (button) {
            const textSpan = button.querySelector('span');
            if (textSpan && !textSpan.querySelector('svg')) {
              textSpan.textContent = `${enConfig.country} | ${currencyText}`;
            }
          }
        }
      });

      // Update price filter currency symbols and max price text
      const currentConfig = self.getCurrencyConfig();
      if (currentConfig) {
        // Update currency symbols in price filter inputs
        document.querySelectorAll('[data-currency-symbol]').forEach((el) => {
          el.textContent = currentConfig.symbol;
        });

        // Update "The highest price is X BD" text
        document.querySelectorAll('[data-max-price]').forEach((el) => {
          const text = el.textContent;
          // Replace any currency code (BD, BHD, etc.) with the current locale's code
          // Match the price number and currency
          const priceMatch = text.match(/([\d.,\s]+)\s*([A-Za-z\u0600-\u06FF.]+)\s*$/);
          if (priceMatch) {
            const originalPrice = parseFloat(priceMatch[1].replace(/,/g, '').replace(/\s/g, ''));
            if (!isNaN(originalPrice)) {
              // Convert price from BHD to target currency
              const decimals = currentConfig.decimals || 3;
              const convertedPrice = (originalPrice * currentConfig.rate).toFixed(decimals);
              const newText = text.replace(priceMatch[0], ` ${convertedPrice} ${currentConfig.code}`);
              el.textContent = newText;
            }
          }
        });
      }
    };

    // Run on init and again after a short delay for lazy-loaded elements
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', updateAll);
    } else {
      updateAll();
    }
    setTimeout(updateAll, 1500);
  }

  /**
   * Override theme.Currency.formatMoney to convert prices
   */
  overrideThemeCurrencyFormatter() {
    const self = this;

    // Only override for non-Bahrain markets
    if (this.currentLocale.endsWith('bh')) {
      return;
    }

    // Wait for theme to be available
    const checkForTheme = setInterval(() => {
      if (window.theme && window.theme.Currency && window.theme.Currency.formatMoney) {
        clearInterval(checkForTheme);

        const originalFormatMoney = window.theme.Currency.formatMoney;

        window.theme.Currency.formatMoney = function (cents, format) {
          // Call original formatter to get BHD formatted string
          const bhdFormatted = originalFormatMoney.call(this, cents, format);

          // Extract the number from the formatted string
          const numberMatch = bhdFormatted.match(/[\d,\.]+/);
          if (!numberMatch) {
            return bhdFormatted; // Return as-is if can't parse
          }

          const bhdAmount = parseFloat(numberMatch[0].replace(/,/g, '.'));

          // Convert from BHD to target currency
          const convertedAmount = self.convertPrice(bhdAmount);

          // Format with target currency - use configured decimals
          const config = self.getCurrencyConfig();
          const decimals = config.decimals || 3;
          const formattedAmount = convertedAmount.toFixed(decimals);

          // Build formatted string with target currency
          // Use LRM for proper RTL display
          const LRM = '\u200E';
          let result;
          if (config.direction === 'rtl') {
            result = `${LRM}${formattedAmount} ${config.symbol}${LRM}`;
          } else {
            result = `${config.symbol} ${formattedAmount}`;
          }

          return result;
        };
      }
    }, 100);

    // Also override Shopify.formatMoney if available
    const checkForShopify = setInterval(() => {
      if (window.Shopify && window.Shopify.formatMoney) {
        clearInterval(checkForShopify);

        const originalShopifyFormat = window.Shopify.formatMoney;

        window.Shopify.formatMoney = function (cents, format) {
          // Call original formatter
          const bhdFormatted = originalShopifyFormat.call(this, cents, format);

          // Extract the number
          const numberMatch = bhdFormatted.match(/[\d,\.]+/);
          if (!numberMatch) {
            return bhdFormatted;
          }

          const bhdAmount = parseFloat(numberMatch[0].replace(/,/g, '.'));

          // Convert from BHD to target currency
          const convertedAmount = self.convertPrice(bhdAmount);

          // Format with target currency - use configured decimals
          const config = self.getCurrencyConfig();
          const decimals = config.decimals || 3;
          const formattedAmount = convertedAmount.toFixed(decimals);

          // Build formatted string with target currency
          // Use LRM for proper RTL display
          const LRM = '\u200E';
          let result;
          if (config.direction === 'rtl') {
            result = `${LRM}${formattedAmount} ${config.symbol}${LRM}`;
          } else {
            result = `${config.symbol} ${formattedAmount}`;
          }

          return result;
        };
      }
    }, 100);

    // Timeout after 5 seconds
    setTimeout(() => {
      clearInterval(checkForTheme);
      clearInterval(checkForShopify);
    }, 5000);
  }

  /**
   * Extract locale from current URL
   */
  getLocaleFromURL() {
    const pathname = window.location.pathname;
    // Match pattern like /en-bh/, /ar-sa/, /en-ae, etc. (with or without trailing slash)
    const match = pathname.match(/\/(en|ar)-(bh|sa|ae|kw|om|qa)(?:\/|$)/);

    if (match) {
      return `${match[1]}-${match[2]}`;
    }

    // Fallback to default locale
    return 'en-bh';
  }

  /**
   * Get currency config for current locale
   */
  getCurrencyConfig(locale = this.currentLocale) {
    return this.currencies[locale] || this.currencies['en-bh'];
  }

  /**
   * Round a price upward based on the configured rounding mode.
   * - 'none' : no rounding (return as-is)
   * - '0.5'  : round up to nearest 0.5  (e.g. 1.135 → 1.5, 1.675 → 2.0)
   * - '1.0'  : round up to nearest 1.0  (e.g. 1.135 → 2.0, 1.675 → 2.0)
   */
  roundPrice(price, mode) {
    if (mode === '0.5') return Math.ceil(price * 2) / 2;
    if (mode === '1.0') return Math.ceil(price);
    return price; // 'none' – no rounding
  }

  /**
   * Convert price from BHD (store currency) to target currency
   */
  convertPrice(priceInBHD) {
    // Input is in BHD (store's base currency)
    // We need to convert it to the target currency
    const config = this.getCurrencyConfig();

    // Since all prices on the page are in BHD, multiply by the exchange rate
    // which is the target currency value relative to BHD
    const convertedPrice = parseFloat(priceInBHD) * config.rate;

    // Apply optional price rounding (ceil to nearest 0.5 or 1.0)
    const rounded = this.roundPrice(convertedPrice, this.roundingMode);

    const decimals = config.decimals || 3;
    return parseFloat(rounded.toFixed(decimals));
  }

  /**
   * Format price with currency symbol
   * Uses Unicode directional marks to ensure proper display in RTL contexts
   * Format: English = "BHD 19.900", Arabic = "19.900 د.ب"
   */
  formatPrice(price, locale = this.currentLocale, asHTML = false) {
    const config = this.getCurrencyConfig(locale);
    const isArabic = locale.startsWith('ar');

    // Use configured decimal places
    const decimals = config.decimals || 3;
    const formattedPrice = price.toFixed(decimals);

    if (isArabic) {
      // For Arabic: price before symbol (e.g., "19.900 د.ب")
      if (asHTML) {
        return `<span dir="ltr" style="unicode-bidi: embed;">${formattedPrice} ${config.symbol}</span>`;
      } else {
        // Use Left-to-Right Mark for proper RTL display
        const LRM = '\u200E';
        return `${LRM}${formattedPrice} ${config.symbol}${LRM}`;
      }
    } else {
      // For English: symbol before price (e.g., "BHD 19.900")
      return `${config.symbol} ${formattedPrice}`;
    }
  }

  /**
   * Format prices for RTL display without conversion (for Arabic Bahrain)
   * This applies RTL-safe formatting to existing BHD prices
   */
  formatPricesForRTL() {
    const config = this.getCurrencyConfig();

    // Price selectors to target
    const priceSelectors = [
      '.price-item',
      '.price-item--regular',
      '.price-item--sale',
      '.product-card__price',
      '.product-single__price',
      '.money',
      '[data-price]',
      '.cart-item__price',
      '.line-item-price',
    ];

    priceSelectors.forEach((selector) => {
      const elements = document.querySelectorAll(`${selector}:not([data-rtl-formatted])`);

      elements.forEach((el) => {
        const text = el.textContent.trim();

        // Check if this looks like a BHD price (with currency code/symbol)
        // Match patterns: "BHD 19.990" or "19.990 BHD" or "د.ب 19.990" or "19.990 د.ب"
        const bhdMatch = text.match(/(?:BHD|د\.ب)\s*([\d]+\.[\d]+)|([\d]+\.[\d]+)\s*(?:BHD|د\.ب)/i);

        if (bhdMatch) {
          const priceStr = bhdMatch[1] || bhdMatch[2];
          // Clean the price string - remove any leading/trailing dots
          const cleanPriceStr = priceStr.replace(/^\.+|\.+$/g, '');
          const price = parseFloat(cleanPriceStr);

          if (price > 0 && price < 1000000 && !isNaN(price)) {
            // Format with Arabic symbol and LTR wrapper - price before symbol
            el.innerHTML = `<span dir="ltr" style="unicode-bidi: embed;">${price.toFixed(3)} ${config.symbol}</span>`;
            el.setAttribute('data-rtl-formatted', 'true');
          }
        } else {
          // Check for just a number (might be a price without currency)
          // Match a number with decimal point like "19.990"
          const numMatch = text.match(/^\s*([\d]+\.[\d]+)\s*$/);
          if (numMatch) {
            const cleanPriceStr = numMatch[1].replace(/^\.+|\.+$/g, '');
            const price = parseFloat(cleanPriceStr);
            if (price > 0.1 && price < 1000000 && !isNaN(price)) {
              el.innerHTML = `<span dir="ltr" style="unicode-bidi: embed;">${price.toFixed(3)} ${config.symbol}</span>`;
              el.setAttribute('data-rtl-formatted', 'true');
            }
          }
        }
      });
    });
  }

  /**
   * Format prices for English display (for English Bahrain)
   * This ensures prices show "BHD X.XXX" format (symbol before price)
   */
  formatPricesForEnglish() {
    const config = this.getCurrencyConfig();

    // Price selectors to target
    const priceSelectors = [
      '.price-item',
      '.price-item--regular',
      '.price-item--sale',
      '.product-card__price',
      '.product-single__price',
      '.money',
      '[data-price]',
      '.cart-item__price',
      '.line-item-price',
    ];

    priceSelectors.forEach((selector) => {
      const elements = document.querySelectorAll(`${selector}:not([data-en-formatted])`);

      elements.forEach((el) => {
        const text = el.textContent.trim();

        // Check if this looks like a BHD price in wrong format: "19.990 BHD" or "19.990BHD"
        // We want to convert to "BHD 19.990"
        const wrongFormatMatch = text.match(/^([\d]+\.[\d]+)\s*BHD/i);

        if (wrongFormatMatch) {
          const priceStr = wrongFormatMatch[1];
          const cleanPriceStr = priceStr.replace(/^\.+|\.+$/g, '');
          const price = parseFloat(cleanPriceStr);

          if (price > 0 && price < 1000000 && !isNaN(price)) {
            // Reformat with symbol BEFORE price
            el.textContent = `${config.symbol} ${price.toFixed(3)}`;
            el.setAttribute('data-en-formatted', 'true');
          }
        } else {
          // Also check for prices already in correct format but maybe wrong decimals
          const correctFormatMatch = text.match(/^BHD\s*([\d]+\.[\d]+)/i);
          if (correctFormatMatch) {
            const priceStr = correctFormatMatch[1];
            const price = parseFloat(priceStr);
            if (price > 0 && price < 1000000 && !isNaN(price)) {
              // Update to ensure 3 decimal places
              el.textContent = `${config.symbol} ${price.toFixed(3)}`;
              el.setAttribute('data-en-formatted', 'true');
            }
          }
        }
      });
    });
  }

  /**
   * Convert all prices on the page
   */
  convertPrices() {
    // Don't convert for Bahrain (base currency)
    if (this.currentLocale.endsWith('bh')) {
      return;
    }

    // Strategy 1: Target price display divs with class product-card__price
    const priceContainers = document.querySelectorAll('.product-card__price:not([data-converted])');

    priceContainers.forEach((container, index) => {
      // Find the parent card to get the data-price value
      const card = container.closest('[data-price]');
      if (!card) {
        return;
      }

      const dataPriceAttr = card.getAttribute('data-price');
      const bhdPrice = parseFloat(dataPriceAttr) / 100;

      if (bhdPrice > 0) {
        const convertedPrice = this.convertPrice(bhdPrice);
        const formattedPrice = this.formatPrice(convertedPrice);

        // Replace ALL text nodes in the price container
        this.replaceTextInNode(container, bhdPrice, formattedPrice);

        container.setAttribute('data-converted', 'true');
      }
    });

    // Strategy 2: Fallback to finding elements with data-price if no price containers found
    if (priceContainers.length === 0) {
      const productCards = document.querySelectorAll('[data-price]:not([data-converted-price])');

      productCards.forEach((card, index) => {
        const dataPriceAttr = card.getAttribute('data-price');

        if (dataPriceAttr) {
          const bhdPrice = parseFloat(dataPriceAttr) / 100;

          if (bhdPrice > 0) {
            const convertedPrice = this.convertPrice(bhdPrice);
            const formattedPrice = this.formatPrice(convertedPrice);

            this.replaceTextInNode(card, bhdPrice, formattedPrice);
            card.setAttribute('data-converted-price', convertedPrice);
          }
        }
      });
    }

    // Strategy 3: Universal price targeting - find all elements with price-like content
    this.convertUniversalPrices();

    // Strategy 4: Specific product page price conversion
    this.convertProductPagePrices();

    // Convert cart prices
    this.convertCartPrices();

    // Convert collection page prices
    this.convertCollectionPrices();
  }

  /**
   * Universal price conversion - finds all price-like content on the page
   */
  convertUniversalPrices() {
    // Skip for Bahrain (base currency)
    if (this.currentLocale.endsWith('bh')) {
      return;
    }

    // Selectors for different price types across pages
    const priceSelectors = [
      '.product-single__price', // Product page prices (Perfumista theme)
      '.price-item', // Product card prices (Perfumista theme)
      '.product-card__price', // Homepage product card prices
      '.js-cart-total', // Mini cart total
      '.mini-cart-item span', // Mini cart item prices
      '.bottom-total span', // Cart drawer total
      '.price',
      '[data-price]',
      '.product-price',
      '.product__price',
      '.money',
      '[data-money]',
      '.product-form__price',
      '.cart-item__price',
      '.line-item-total',
      '.line-item-price',
      '.order-summary__subtotal',
      '.order-summary__total',
      '[data-line-item-price]',
      '[data-cart-total]',
    ];

    let totalConversions = 0;

    priceSelectors.forEach((selector) => {
      const elements = document.querySelectorAll(`${selector}:not([data-currency-converted])`);

      if (elements.length > 0) {
        elements.forEach((el) => {
          // Skip if already converted
          if (el.getAttribute('data-currency-converted')) {
            return;
          }

          const text = el.textContent.trim();

          // Check if this looks like a price in multiple formats:
          // 1. "BHD 100" or "100 BHD" or "AED 100" or "100 AED" or "Ø¯.Ø¥ 100"
          // 2. Just a number like "100" or "100.50" or "100,50"
          // 3. Currency symbol + number like "$100" or "100 $"
          let priceMatch = text.match(
            /(?:BHD|AED|Ø¯\.Ø¥)\s*([\d,\.]+)|([\d,\.]+)\s*(?:BHD|AED|Ø¯\.Ø¥)|^\s*([\d,\.]+)\s*$/,
          );

          if (!priceMatch && /^\s*[\d,\.]+\s*$/.test(text)) {
            // It's just a number - treat as price
            priceMatch = text.match(/([\d,\.]+)/);
          }

          if (priceMatch) {
            // Extract the price value from any match group
            let priceStr = priceMatch[1] || priceMatch[2] || priceMatch[3] || priceMatch[0];
            const bhdPrice = parseFloat(priceStr.replace(/,/g, '.'));

            // Only convert if it looks like a reasonable price (between 0.1 and 1000000)
            if (bhdPrice > 0.1 && bhdPrice < 1000000) {
              const convertedPrice = this.convertPrice(bhdPrice);
              const isArabic = this.currentLocale.startsWith('ar');

              // Only replace if this is a leaf element (no children) or only has text
              if (el.children.length === 0 || (el.textContent && !el.innerHTML.includes('<'))) {
                if (isArabic) {
                  // Use HTML with dir="ltr" span for Arabic
                  el.innerHTML = this.formatPrice(convertedPrice, this.currentLocale, true);
                } else {
                  el.textContent = this.formatPrice(convertedPrice);
                }
              } else {
                // If has children, try to replace text nodes
                const formattedPrice = this.formatPrice(convertedPrice);
                this.replaceTextInNode(el, bhdPrice, formattedPrice);
              }

              el.setAttribute('data-currency-converted', 'true');
              totalConversions++;
            }
          }
        });
      }
    });

    // Special handler for .product-card__price (homepage products)

    const productCardPrices = document.querySelectorAll('.product-card__price:not([data-currency-converted])');

    productCardPrices.forEach((priceDiv, index) => {
      // Get all text nodes from the price div
      const textContent = priceDiv.textContent.trim();

      // Look for BHD price pattern - match "BHD XXX.XXX" or "BHD XXX,XXX"
      const bhdMatch = textContent.match(/BHD\s*([\d,\.]+)/i);
      if (bhdMatch) {
        const bhdPriceStr = bhdMatch[1];
        const bhdPrice = parseFloat(bhdPriceStr.replace(/,/g, '.'));

        if (bhdPrice > 0.1 && bhdPrice < 1000000) {
          const convertedPrice = this.convertPrice(bhdPrice);
          const formattedPrice = this.formatPrice(convertedPrice);

          // Replace the text in the div while preserving structure
          this.replaceTextInNode(priceDiv, `BHD ${bhdPriceStr}`, formattedPrice);

          priceDiv.setAttribute('data-currency-converted', 'true');
          totalConversions++;
        }
      } else {
      }
    });
  }

  /**
   * Convert prices specifically on product pages
   * Handles the .product-single__price element and related price spans
   */
  convertProductPagePrices() {
    // Skip for Bahrain (base currency)
    if (this.currentLocale.endsWith('bh')) {
      return;
    }

    // Find all product single price elements
    const productPrices = document.querySelectorAll('.product-single__price:not([data-currency-converted])');

    productPrices.forEach((priceEl, index) => {
      // Check if element has a data-price attribute (in cents)
      const dataPrice = priceEl.getAttribute('data-price');
      if (dataPrice) {
        const bhdPrice = parseFloat(dataPrice) / 100;

        if (bhdPrice > 0.1 && bhdPrice < 1000000) {
          const convertedPrice = this.convertPrice(bhdPrice);
          const formattedPrice = this.formatPrice(convertedPrice);

          // Get the text content and replace the price
          const textContent = priceEl.textContent.trim();

          // Find the price number in the text (handles formats like "100.00" or "100,00")
          const numberMatch = textContent.match(/([\d,\.]+)/);
          if (numberMatch) {
            const oldPriceStr = numberMatch[0];
            const newText = textContent.replace(oldPriceStr, formattedPrice);
            priceEl.textContent = newText;
          } else {
            // If no number found, just replace the whole thing
            priceEl.textContent = formattedPrice;
          }

          priceEl.setAttribute('data-currency-converted', 'true');
        }
      } else {
        // No data-price attribute, try to extract from text content
        const textContent = priceEl.textContent.trim();

        // Look for currency prefix patterns or plain numbers
        let bhdPrice = null;
        let originalPriceStr = null;

        // Pattern 1: "BHD 100" or "BHD100"
        const bhdPattern = textContent.match(/BHD\s*([\d,\.]+)/i);
        if (bhdPattern) {
          originalPriceStr = bhdPattern[0];
          bhdPrice = parseFloat(bhdPattern[1].replace(/,/g, '.'));
        }

        // Pattern 2: "100 BHD" or "100BHD"
        if (!bhdPrice) {
          const bhdPattern2 = textContent.match(/([\d,\.]+)\s*BHD/i);
          if (bhdPattern2) {
            originalPriceStr = bhdPattern2[0];
            bhdPrice = parseFloat(bhdPattern2[1].replace(/,/g, '.'));
          }
        }

        // Pattern 3: Just a number (assume it's in BHD)
        if (!bhdPrice) {
          const numPattern = textContent.match(/^([\d,\.]+)$/);
          if (numPattern) {
            originalPriceStr = numPattern[0];
            bhdPrice = parseFloat(numPattern[1].replace(/,/g, '.'));
          }
        }

        if (bhdPrice && bhdPrice > 0.1 && bhdPrice < 1000000) {
          const convertedPrice = this.convertPrice(bhdPrice);
          const formattedPrice = this.formatPrice(convertedPrice);

          // Replace in the text
          const newText = textContent.replace(originalPriceStr || bhdPrice.toString(), formattedPrice);
          priceEl.textContent = newText;
          priceEl.setAttribute('data-currency-converted', 'true');
        }
      }
    });

    // Also handle compare prices
    const comparePrices = document.querySelectorAll('.product-single__price--compare:not([data-currency-converted])');

    comparePrices.forEach((priceEl, index) => {
      const textContent = priceEl.textContent.trim();
      const numMatch = textContent.match(/([\d,\.]+)/);

      if (numMatch) {
        const bhdPrice = parseFloat(numMatch[1].replace(/,/g, '.'));

        if (bhdPrice > 0.1 && bhdPrice < 1000000) {
          const convertedPrice = this.convertPrice(bhdPrice);
          const formattedPrice = this.formatPrice(convertedPrice);

          const newText = textContent.replace(numMatch[1], formattedPrice);
          priceEl.textContent = newText;
          priceEl.setAttribute('data-currency-converted', 'true');
        }
      }
    });
  }

  /**
   * Replace text nodes matching a price with new formatted price

   */
  replaceTextInNode(node, originalPrice, newPrice) {
    const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT, null, false);

    let textNode;
    const nodesToReplace = [];

    // Collect all text nodes that look like prices
    while ((textNode = walker.nextNode())) {
      const text = textNode.textContent.trim();

      // Check if this text contains currency or numbers that look like prices
      if (text.includes('BHD') || /[\d,\.]+/.test(text)) {
        nodesToReplace.push({
          node: textNode,
          originalText: textNode.textContent,
          text: text,
        });
      }
    }

    // Replace the text nodes
    nodesToReplace.forEach((item, i) => {
      let newText = item.originalText;

      // Try different replacement strategies in order

      // Strategy 1: Replace "BHD ###" pattern
      if (/BHD\s+[\d,\.]+/i.test(item.originalText)) {
        newText = item.originalText.replace(/BHD\s+[\d,\.]+/i, newPrice);
      }

      // Strategy 2: Replace "### BHD" pattern
      else if (/[\d,\.]+\s+BHD/i.test(item.originalText)) {
        newText = item.originalText.replace(/[\d,\.]+\s+BHD/i, newPrice);
      }

      // Strategy 3: If text is JUST a price number, replace it entirely
      else if (/^[\d,\.]+$/.test(item.text)) {
        newText = newPrice;
      }

      // Only update if something changed
      if (newText !== item.originalText) {
        item.node.textContent = newText;
      }
    });
  }

  /**
   * Extract USD price from text - improved to handle Shopify money formats
   */
  extractUSDPrice(priceText) {
    // Remove currency symbols and extra whitespace
    let cleaned = priceText.trim();

    // Remove common currency symbols: $, Ø¯.Ø¨, Ø±.Ø³, Ø¯.Ø¥, Ø¯.Ùƒ, Ø±.Ø¹., Ø±.Ù‚, etc
    cleaned = cleaned.replace(/[\$\d.Ø¨\s,Ø±\-Ø¥ÙƒØ¹Ù‚\.]/g, '');

    // Extract first number sequence (digits with optional decimal point)
    const match = cleaned.match(/(\d+\.?\d*)/);
    return match ? parseFloat(match[1]) : null;
  }

  /**
   * Convert cart prices
   * Targets the Dawn theme cart-drawer and cart page selectors
   */
  convertCartPrices() {
    // Don't convert for Bahrain
    if (this.currentLocale.endsWith('bh')) {
      return;
    }

    const self = this;
    const isArabic = this.currentLocale.startsWith('ar');

    // Regex to match prices with "BD", "BHD", or Arabic symbols
    const priceRegex = /(?:BHD|BD)\s*([\d,\.]+)|([\d,\.]+)\s*(?:BHD|BD)/i;
    const plainNumberRegex = /^\s*([\d,\.]+)\s*$/;

    /**
     * Extract BHD price from text, convert and return formatted string.
     */
    const convertText = (text) => {
      let priceStr = null;

      const match = text.match(priceRegex);
      if (match) {
        priceStr = match[1] || match[2];
      } else {
        const numMatch = text.match(plainNumberRegex);
        if (numMatch) priceStr = numMatch[1];
      }

      if (!priceStr) return null;

      const bhdPrice = parseFloat(priceStr.replace(/,/g, '.'));
      if (isNaN(bhdPrice) || bhdPrice <= 0 || bhdPrice >= 1000000) return null;

      const convertedPrice = self.convertPrice(bhdPrice);
      return self.formatPrice(convertedPrice);
    };

    // --- Cart drawer & cart page selectors ---
    const cartPriceSelectors = [
      // Dawn cart-drawer line item prices
      '.cart-item__old-price',
      '.cart-item__final-price',
      '.cart-item__totals .price',
      '.cart-item__price-wrapper .price',
      // Product option price (under product name)
      '.cart-item__details .product-option',
      // Cart total
      '.totals__total-value',
      // Discount amounts
      '.discounts__discount',
      // Full cart page
      '.cart-item__price',
      '[data-line-item-price]',
      '.line-item-price',
      // Mini-cart / legacy
      '.js-cart-total',
      '.mini-cart-item span',
      '.bottom-total span',
      '.totals .price',
      '.cart__subtotal',
    ];

    const selector = cartPriceSelectors.map((s) => `${s}:not([data-cart-converted])`).join(', ');

    document.querySelectorAll(selector).forEach((el) => {
      const text = el.textContent.trim();
      if (!text) return;

      // Handle discount elements (keep icon + title, only convert price part)
      if (el.classList.contains('discounts__discount')) {
        const pricePattern = /\(?\-?\s*(?:(?:BHD|BD)\s*[\d,\.]+|[\d,\.]+\s*(?:BHD|BD))\)?/i;
        const priceInText = text.match(pricePattern);
        if (priceInText) {
          const cleaned = priceInText[0].replace(/[()\\-\s]/g, '');
          const result = convertText(cleaned);
          if (result) {
            el.innerHTML = el.innerHTML.replace(priceInText[0], `(-${result})`);
            el.setAttribute('data-cart-converted', 'true');
          }
        }
        return;
      }

      const result = convertText(text);
      if (!result) return;

      if (el.children.length === 0) {
        if (isArabic) {
          const match = text.match(priceRegex) || text.match(plainNumberRegex);
          const priceStr = match ? match[1] || match[2] || match[0] : '0';
          const bhdPrice = parseFloat(priceStr.replace(/,/g, '.'));
          if (bhdPrice > 0) {
            el.innerHTML = self.formatPrice(self.convertPrice(bhdPrice), self.currentLocale, true);
          }
        } else {
          el.textContent = result;
        }
      } else {
        self.replaceTextInNode(el, null, result);
      }

      el.setAttribute('data-cart-converted', 'true');
    });

    // --- Fix: Recalculate Cart Total ---
    // The cart total in BHD might round slightly differently than the sum of the individually rounded line items.
    // By calculating the sum of the displayed line items, we avoid any math mismatch illusions.
    setTimeout(() => {
      let activeTotal = 0;
      let itemsFound = 0;

      // Ensure we only sum from ONE container type to prevent double counting 
      // when both cart drawer and cart page are present in the DOM.
      let cartContainer = document.querySelector('cart-drawer.is-empty') ? null : document.querySelector('cart-drawer');
      if (!cartContainer || cartContainer.querySelectorAll('.cart-item__totals').length === 0) {
        cartContainer = document.querySelector('cart-items');
      }

      if (cartContainer) {
        // Find the final line prices within the active container only
        // Use span.price.price--end to avoid capturing <s> elements (original prices before discounts)
        const lineTotals = cartContainer.querySelectorAll('.cart-item__totals span.price.price--end');
        
        lineTotals.forEach((el) => {
          const text = el.textContent || el.innerText;
          const numMatch = text.match(/[\d,\.]+/);
          if (numMatch) {
            const val = parseFloat(numMatch[0].replace(/,/g, ''));
            if (!isNaN(val)) {
              activeTotal += val;
              itemsFound++;
            }
          }
        });

        if (itemsFound > 0) {
          // Subtract any cart-level discounts from the corresponding footer
          let footerContainer = cartContainer.tagName.toLowerCase() === 'cart-drawer' 
            ? cartContainer 
            : document.querySelector('#main-cart-footer');
            
          if (footerContainer) {
            const discounts = footerContainer.querySelectorAll('.discounts__discount');
            discounts.forEach((el) => {
              const text = el.textContent || el.innerText;
              const numMatch = text.match(/[\d,\.]+/);
              if (numMatch) {
                const val = parseFloat(numMatch[0].replace(/,/g, ''));
                if (!isNaN(val)) {
                  activeTotal -= val;
                }
              }
            });
          }

          activeTotal = Math.max(0, activeTotal);

          // Update the total elements globally across the page
          const totalEls = document.querySelectorAll('.totals__total-value, .cart__subtotal .price');
          totalEls.forEach((el) => {
            if (isArabic) {
              el.innerHTML = self.formatPrice(activeTotal, self.currentLocale, true);
            } else {
              el.textContent = self.formatPrice(activeTotal);
            }
            el.setAttribute('data-cart-converted', 'true');
          });
        }
      }
    }, 50);

    // --- Cart drawer collection mini-card prices ---
    // These use data-price (in cents) set by Liquid, so we convert directly
    // rather than relying on regex matching of the formatted money string.
    document.querySelectorAll('.cart-drawer__mini-card-price[data-price]:not([data-cart-converted])').forEach((el) => {
      const cents = parseFloat(el.getAttribute('data-price'));
      if (isNaN(cents) || cents <= 0) return;

      const bhdPrice = cents / 100;
      const convertedPrice = self.convertPrice(bhdPrice);

      if (isArabic) {
        el.innerHTML = self.formatPrice(convertedPrice, self.currentLocale, true);
      } else {
        el.textContent = self.formatPrice(convertedPrice);
      }

      el.setAttribute('data-cart-converted', 'true');
    });
  }

  /**
   * Convert collection page prices
   */
  convertCollectionPrices() {
    // Don't convert for Bahrain
    if (this.currentLocale.endsWith('bh')) {
      return;
    }

    // This is now handled by convertPrices() which processes all [data-price] elements
    // This method is kept for backwards compatibility
  }

  /**
   * Observe DOM for dynamic content changes
   */
  observeDOM() {
    const observer = new MutationObserver((mutations) => {
      let shouldConvert = false;

      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' || mutation.type === 'characterData') {
          // Check if new price elements were added
          if (mutation.addedNodes.length > 0) {
            mutation.addedNodes.forEach((node) => {
              if (node.nodeType === 1) {
                // Element node
                if (
                  node.classList &&
                  (node.classList.contains('price') ||
                    node.classList.contains('price-item') ||
                    node.classList.contains('product-price') ||
                    node.classList.contains('product-single__price') ||
                    node.classList.contains('money') ||
                    node.classList.contains('product__price') ||
                    node.classList.contains('product-form__price') ||
                    node.classList.contains('line-item-price') ||
                    node.classList.contains('card-wrapper') ||
                    node.classList.contains('product-card__price') ||
                    node.classList.contains('cart-item') ||
                    node.classList.contains('cart-item__details') ||
                    node.classList.contains('cart-item__totals') ||
                    node.classList.contains('cart-item__price-wrapper') ||
                    node.classList.contains('totals') ||
                    node.classList.contains('totals__total-value') ||
                    node.classList.contains('drawer__contents') ||
                    node.classList.contains('cart-drawer__form') ||
                    node.getAttribute('data-price') ||
                    node.getAttribute('data-money') ||
                    node.getAttribute('data-line-item-price') ||
                    node.getAttribute('data-cart-total'))
                ) {
                  shouldConvert = true;
                }
                // Also check descendants
                if (
                  node.querySelector &&
                  node.querySelector(
                    '[data-price], [data-money], .price, .price-item, .product-card__price, .product-single__price, .product-price, .money, .product__price, .product-form__price, .line-item-price, [data-line-item-price], [data-cart-total], .cart-item, .cart-item__details, .cart-item__totals, .totals__total-value, .totals, .drawer__contents',
                  )
                ) {
                  shouldConvert = true;
                }
              }
            });
          }
        }
      });

      if (shouldConvert) {
        // Debounce the conversion
        clearTimeout(this.convertTimeout);
        this.convertTimeout = setTimeout(() => {
          if (!this.currentLocale.endsWith('bh')) {
            this.convertPrices();
            this.convertCartPrices();
          } else if (this.currentLocale.startsWith('ar')) {
            this.formatPricesForRTL();
          } else if (this.currentLocale.startsWith('en')) {
            this.formatPricesForEnglish();
          }
        }, 100);
      }
    });

    // Start observing the document
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  /**
   * Store locale in localStorage
   */
  setStoredLocale(locale) {
    try {
      localStorage.setItem('perfumista_locale', locale);
    } catch (e) {}
  }

  /**
   * Get stored locale
   */
  getStoredLocale() {
    try {
      return localStorage.getItem('perfumista_locale') || this.currentLocale;
    } catch (e) {
      return this.currentLocale;
    }
  }

  /**
   * Get all available currencies
   */
  getAvailableCurrencies() {
    return this.currencies;
  }

  /**
   * Get current currency info
   */
  getCurrentCurrency() {
    return {
      locale: this.currentLocale,
      ...this.getCurrencyConfig(),
    };
  }

  /**
   * Update currency info in DOM
   */
  updateCurrencyInfo() {
    const currency = this.getCurrentCurrency();
    const currencyInfo = document.querySelector('[data-currency-info]');

    if (currencyInfo) {
      currencyInfo.setAttribute('data-currency-code', currency.code);
      currencyInfo.setAttribute('data-currency-symbol', currency.symbol);
      currencyInfo.setAttribute('data-currency-locale', currency.locale);
    }

    // Update data attributes on body for CSS hooks
    document.documentElement.setAttribute('data-currency', currency.code);
    document.documentElement.setAttribute('data-locale', currency.locale);

    // Update direction and language for the entire page
    const config = this.getCurrencyConfig();
    const direction = config.direction || (currency.locale.startsWith('ar') ? 'rtl' : 'ltr');
    const language = currency.locale.split('-')[0]; // en or ar

    document.documentElement.setAttribute('dir', direction);
    document.documentElement.setAttribute('lang', language);
    document.body.setAttribute('dir', direction);

    // Dispatch custom event for RTL/language change
    const rtlChangeEvent = new CustomEvent('perfumista:rtl-change', {
      detail: {
        isRTL: direction === 'rtl',
        locale: currency.locale,
        direction: direction,
        language: language,
      },
      bubbles: true,
    });
    document.dispatchEvent(rtlChangeEvent);
  }

  /**
   * Setup observer for Shopify cart updates
   */
  setupCartObserver() {
    const self = this;

    // Listen for cart updates
    document.addEventListener('cart:updated', (e) => {
      if (e.detail && e.detail.cart) {
        setTimeout(() => {
          self.convertCartPrices();
          self.convertPrices();
        }, 100);
      }
    });

    // Watch for theme.miniCart updates
    if (window.theme && window.theme.miniCart) {
      const originalUpdateElements = window.theme.miniCart.updateElements;
      const originalGenerateCart = window.theme.miniCart.generateCart;

      if (typeof originalUpdateElements === 'function') {
        window.theme.miniCart.updateElements = function () {
          originalUpdateElements.apply(this, arguments);
          setTimeout(() => {
            self.convertCartPrices();
            self.convertPrices();
          }, 100);
        };
      }

      if (typeof originalGenerateCart === 'function') {
        window.theme.miniCart.generateCart = function () {
          originalGenerateCart.apply(this, arguments);
          setTimeout(() => {
            self.convertCartPrices();
            self.convertPrices();
          }, 100);
        };
      }
    }

    // Also watch for Shopify AJAX API cart changes
    if (window.Shopify && window.Shopify.cart) {
      const originalFetch = window.Shopify.cart.fetch;

      if (typeof originalFetch === 'function') {
        window.Shopify.cart.fetch = function () {
          const result = originalFetch.apply(this, arguments);
          if (result && typeof result.then === 'function') {
            result.then(() => {
              setTimeout(() => {
                self.convertCartPrices();
                self.convertPrices();
              }, 100);
            });
          }
          return result;
        };
      }
    }
  }

  /**
   * Get locale parts (language and country)
   */
  getLocaleParts(locale = this.currentLocale) {
    const parts = locale.split('-');
    return {
      language: parts[0],
      country: parts[1],
    };
  }

  /**
   * Check if current locale is Arabic
   */
  isArabic(locale = this.currentLocale) {
    return locale.startsWith('ar');
  }

  /**
   * Get market info
   */
  getMarketInfo(locale = this.currentLocale) {
    const config = this.getCurrencyConfig(locale);
    const parts = this.getLocaleParts(locale);

    return {
      locale,
      language: parts.language,
      country: parts.country,
      countryName: parts.language === 'ar' ? config.countryAr : config.country,
      currency: config.code,
      symbol: config.symbol,
      rate: config.rate,
      direction: config.direction,
    };
  }

  /**
   * Manually set locale and refresh prices
   */
  setLocale(locale) {
    if (this.currencies[locale]) {
      this.currentLocale = locale;
      this.setStoredLocale(locale);

      // Navigate to locale-specific market URL to actually change the market
      this.switchMarket(locale);

      return true;
    }
    return false;
  }

  /**
   * Switch to a different market/locale using Shopify's native localization form
   */
  switchMarket(locale) {
    // Map locale codes to Shopify country codes (ISO 3166-1 alpha-2)
    const countryMap = {
      bh: 'BH',
      sa: 'SA',
      ae: 'AE',
      kw: 'KW',
      om: 'OM',
      qa: 'QA',
      us: 'US',
      gb: 'GB',
      eu: 'DE',
      au: 'AU',
      ca: 'CA',
      ind: 'IN',
    };

    const languageMap = {
      en: 'en',
      ar: 'ar',
    };

    const parts = locale.split('-');
    const lang = parts[0];
    const country = parts[1];

    const shopifyCountry = countryMap[country];
    const shopifyLang = languageMap[lang] || 'en';

    if (!shopifyCountry) {
      console.warn('CurrencyConverter: Unknown country code:', country);
      return;
    }

    // Submit Shopify's native localization form
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/localization';
    form.style.display = 'none';

    // Add the _method hidden field (Shopify expects this)
    const methodInput = document.createElement('input');
    methodInput.type = 'hidden';
    methodInput.name = '_method';
    methodInput.value = 'PUT';
    form.appendChild(methodInput);

    // Add country code
    const countryInput = document.createElement('input');
    countryInput.type = 'hidden';
    countryInput.name = 'country_code';
    countryInput.value = shopifyCountry;
    form.appendChild(countryInput);

    // Add language code
    const langInput = document.createElement('input');
    langInput.type = 'hidden';
    langInput.name = 'language_code';
    langInput.value = shopifyLang;
    form.appendChild(langInput);

    // Add return URL (stay on current page)
    const returnInput = document.createElement('input');
    returnInput.type = 'hidden';
    returnInput.name = 'return_to';
    returnInput.value = window.location.pathname + window.location.search;
    form.appendChild(returnInput);

    document.body.appendChild(form);
    form.submit();
  }

  /**
   * Reset to default currency (Bahrain BHD - no conversion)
   */
  resetToDefault() {
    this.currentLocale = 'en-bh';
    this.setStoredLocale('en-bh');

    // Navigate to Bahrain market via Shopify localization
    this.switchMarket('en-bh');

    return true;
  }

  /**
   * Reset all prices to original (no conversion)
   */
  resetPrices() {
    // Reset product prices
    const priceElements = document.querySelectorAll('[data-price], .price, .product-price, [data-productprice]');

    priceElements.forEach((el) => {
      const originalPrice = el.getAttribute('data-original-price');
      const dataPriceAttr = el.getAttribute('data-price');

      let priceToUse = null;
      if (originalPrice) {
        priceToUse = parseFloat(originalPrice);
      } else if (dataPriceAttr) {
        priceToUse = parseFloat(dataPriceAttr);
      }

      if (priceToUse) {
        // Format without conversion (BHD)
        const formatted = this.formatPrice(priceToUse, 'en-bh');
        el.textContent = formatted;
        el.removeAttribute('data-converted-price');
        if (!el.getAttribute('data-original-price')) {
          el.setAttribute('data-original-price', priceToUse);
        }
      }
    });

    // Reset cart prices
    this.resetCartPrices();

    // Reset collection prices
    this.resetCollectionPrices();
  }

  /**
   * Reset cart prices to original
   */
  resetCartPrices() {
    const cartTotal = document.querySelector('[data-cart-total], .cart-total');
    if (cartTotal) {
      const originalPrice = cartTotal.getAttribute('data-original-price');
      if (originalPrice) {
        const formatted = this.formatPrice(parseFloat(originalPrice), 'en-bh');
        cartTotal.textContent = formatted;
      }
    }

    const lineItems = document.querySelectorAll('[data-line-item-price], .line-item-price');
    lineItems.forEach((item) => {
      const originalPrice = item.getAttribute('data-original-price');
      const dataPriceAttr = item.getAttribute('data-price');

      let priceToUse = null;
      if (originalPrice) {
        priceToUse = parseFloat(originalPrice);
      } else if (dataPriceAttr) {
        priceToUse = parseFloat(dataPriceAttr);
      }

      if (priceToUse) {
        const formatted = this.formatPrice(priceToUse, 'en-bh');
        item.textContent = formatted;
        if (!item.getAttribute('data-original-price')) {
          item.setAttribute('data-original-price', priceToUse);
        }
      }
    });
  }

  /**
   * Reset collection prices to original
   */
  resetCollectionPrices() {
    const productCards = document.querySelectorAll('[data-product-card], .product-card, .product-item');

    productCards.forEach((card) => {
      const priceEl = card.querySelector('[data-price], .price, .product-price');
      if (priceEl) {
        const originalPrice = priceEl.getAttribute('data-original-price');
        const dataPriceAttr = priceEl.getAttribute('data-price');

        let priceToUse = null;
        if (originalPrice) {
          priceToUse = parseFloat(originalPrice);
        } else if (dataPriceAttr) {
          priceToUse = parseFloat(dataPriceAttr);
        }

        if (priceToUse) {
          const formatted = this.formatPrice(priceToUse, 'en-bh');
          priceEl.textContent = formatted;
          if (!priceEl.getAttribute('data-original-price')) {
            priceEl.setAttribute('data-original-price', priceToUse);
          }
        }
      }
    });
  }

  /**
   * Get all market locales
   */
  getMarkets() {
    const markets = {};
    Object.keys(this.currencies).forEach((locale) => {
      const parts = this.getLocaleParts(locale);
      const country = parts.country.toUpperCase();

      if (!markets[country]) {
        markets[country] = [];
      }
      markets[country].push(locale);
    });
    return markets;
  }
}

// Initialize currency converter when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.currencyConverter = new CurrencyConverter();
  });
} else {
  window.currencyConverter = new CurrencyConverter();
}

// Expose globally for debugging
if (typeof window !== 'undefined') {
  window.CurrencyConverter = CurrencyConverter;
}
